import { ref, reactive } from 'vue'
import { subMonths, format, addDays, subDays, parse } from 'date-fns'

export function useAnalytics() {
  // ===== 響應式狀態管理 =====

  // 原本的 chartParams
  const chartParams = reactive({
    start: format(subMonths(new Date(), 1), 'yyyy/MM/dd'),
    end: format(new Date(), 'yyyy/MM/dd'),
    previousStart: format(subMonths(new Date(), 2), 'yyyy/MM/dd'),
    previousEnd: format(subMonths(new Date(), 1), 'yyyy/MM/dd'),
    source: 'all_data',
    postIds: [],
  })

  // ===== 快取機制 =====
  const CACHE_DURATION = 5 * 60 * 1000 // 5分鐘快取
  const CACHE_PREFIX = 'analytics_cache_'
  
  // 使用 sessionStorage 實作持久化快取
  const cacheStorage = {
    get: (key) => {
      try {
        const item = sessionStorage.getItem(CACHE_PREFIX + key)
        return item ? JSON.parse(item) : null
      } catch (e) {
        console.warn('Failed to get cache item:', e)
        return null
      }
    },
    set: (key, value) => {
      try {
        sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value))
      } catch (e) {
        console.warn('Failed to set cache item:', e)
      }
    },
    clear: () => {
      try {
        const keys = Object.keys(sessionStorage).filter(key => key.startsWith(CACHE_PREFIX))
        keys.forEach(key => sessionStorage.removeItem(key))
        return keys.length
      } catch (e) {
        console.warn('Failed to clear cache:', e)
        return 0
      }
    },
    size: () => {
      try {
        return Object.keys(sessionStorage).filter(key => key.startsWith(CACHE_PREFIX)).length
      } catch (e) {
        return 0
      }
    }
  }
  
  // 暴露到 window 供調試使用
  if (typeof window !== 'undefined') {
    window.analyticsCache = cacheStorage
    window.analyticsCacheSize = () => cacheStorage.size()
  }

  // chartData
  const chartData = ref({})

  // sortDirection
  const sortDirection = ref({})

  // 響應式狀態
  const loading = ref(false)
  const posts = ref([])
  const summaryData = ref({})
  const tableData = ref([])
  const error = ref(null)

  // 展開收起狀態管理
  const expandedPosts = ref({})
  const revenueDetails = ref({})

  // ===== API 請求方法 =====
  
  // 獲取模型列表
  const fetchModels = async () => {
    loading.value = true
    error.value = null
    
    try {
      const response = await fetch('/api/v1/profile/all_models', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      posts.value = data || []
      
      // 獲取模型後自動獲取分析數據
      await fetchAnalyticsData()
      
    } catch (err) {
      console.error('Failed to fetch models:', err)
      error.value = err.message
      posts.value = []
    } finally {
      loading.value = false
    }
  }

  // 生成快取鍵值
  const generateCacheKey = (params) => {
    return `analytics_${params.toString()}`
  }

  // 檢查快取是否有效
  const isCacheValid = (cacheEntry) => {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION
  }

  // 統一的數據獲取方法
  const fetchAnalyticsData = async () => {
    if (posts.value.length === 0 && chartParams.postIds.length === 0) {
      console.warn('No models available, skipping analytics data fetch');
      return;
    }

    calculatePreviousDateRange();

    const params = new URLSearchParams({
      start_at: chartParams.start,
      end_at: format(addDays(parse(chartParams.end, 'yyyy/MM/dd', new Date()), 1), 'yyyy/MM/dd'),
      previous_start_at: chartParams.previousStart,
      previous_end_at: chartParams.previousEnd,
    });

    const cacheKey = generateCacheKey(params)
    const cachedData = cacheStorage.get(cacheKey)

    // 檢查快取
    if (isCacheValid(cachedData)) {
      const data = cachedData.data;
      
      // Update all reactive states from cached data
      summaryData.value = {
        data: data.summary_data,
        growth: data.growth_data || {},
        isEmpty: Object.keys(data.summary_data).length === 0
      };
      tableData.value = data.individual_post_data || [];
      chartData.value = data.chart_data || {};
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const api_url = `/api/v1/analysis/all_data?`;
      const response = await fetch(api_url + params);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // 儲存到快取
      cacheStorage.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });

      // Update all reactive states from the API call
      summaryData.value = {
        data: data.summary_data,
        growth: data.growth_data || {},
        isEmpty: Object.keys(data.summary_data).length === 0
      };
      tableData.value = data.individual_post_data || [];
      chartData.value = data.chart_data || {};

    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
      error.value = err.message;
    } finally {
      loading.value = false;
    }
  };

  const fetchIndividualPostRevenuesDetails = async (startDate, endDate, postId) => {
    try {
      const params = new URLSearchParams({
        start_at: startDate,
        end_at: format(addDays(parse(endDate, 'yyyy/MM/dd', new Date()), 1), 'yyyy/MM/dd')
      });

      const response = await fetch(`/api/v1/analysis/post_revenues/${postId}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to access this post\'s revenue details');
        } else if (response.status === 404) {
          throw new Error('Post not found');
        } else if (response.status >= 500) {
          throw new Error('Server error occurred while fetching revenue details');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.individual_post_revenues)) {
        console.warn('Invalid data format received:', data);
        return { individual_post_revenues: [] };
      }

      return data;

    } catch (error) {
      console.error('Failed to fetch individual post revenue details:', error);

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Please check your internet connection');
      }

      throw error;
    }
  };

    // ===== 數據處理方法 =====

  const getDateRange = (start, end) => {
    const dateStart = new Date(start)
    const dateEnd = new Date(end)
    let arr = []
    for (dateStart; dateStart <= dateEnd; dateStart.setDate(dateStart.getDate() + 1)) {
      arr.push(format(new Date(dateStart), 'yyyy/MM/dd'))
    }
    return arr
  }

  const getTotalList = (labels, chartItems, type) => {
    return labels.map((date) => {
      if (chartItems[date]) {
        return chartItems[date]
          .map((item) => item[type])
          .reduce((acc, current) => acc + parseFloat(current), 0)
      } else {
        return 0
      }
    })
  }

  // ===== 統計概覽處理方法 =====

  // 獲取空的模型數據
  const getEmptyModelsData = () => {
    return {
      'views': 0,
      'revenues': 0,
      'likes': 0,
      'download': 0,
      'saved': 0,
    }
  }

  // 計算增長率
  const calculateGrowthRates = (currentData, previousData) => {
    const growth = {}
    Object.keys(currentData).forEach(key => {
      const current = currentData[key]
      const previous = previousData[key] || 0
      
      if (previous === 0) {
        growth[key] = current > 0 ? 'Infinity' : '0%'
      } else if (!isFinite((((current - previous) / previous) * 100))) {
        growth[key] = ''
      } else {
        growth[key] = (((current - previous) / previous) * 100).toFixed(1) + '%'
      }
    })
    return growth
  }

  // 獲取增長率的 CSS 類別
  const getGrowthRateClass = (growthValue) => {
    if (!growthValue || growthValue === 'Infinity' || growthValue === '') {
      return 'infinity'
    }
    
    const numericValue = parseFloat(growthValue)
    if (isNaN(numericValue)) {
      return 'steady'
    }
    
    return numericValue > 0 ? 'increase' : numericValue < 0 ? 'decline' : 'steady'
  }

  // 獲取列的 span 類別
  const getColSpanClass = (index) => {
    return index < 2 ? 'col-span-3' : 'col-span-2'
  }

  // ===== 表格排序方法 =====

  // 解析包含 K 後綴的數值
  const parseMetricValue = (value) => {
    if (typeof value === 'string' && value.includes('K')) {
      return parseFloat(value.replace('K', '')) * 1000
    }
    return parseFloat(value) || 0
  }

  // 表格排序邏輯
  const sortPostsBy = (sortCriteria) => {
    if (sortDirection.value[sortCriteria]) {
      sortDirection.value[sortCriteria] = sortDirection.value[sortCriteria] === 'asc' ? 'desc' : 'asc'
    } else {
      // 清除之前的排序
      Object.keys(sortDirection.value).forEach(key => delete sortDirection.value[key])
      sortDirection.value[sortCriteria] = 'desc'
    }
  }

  // 獲取排序後的表格數據
  const getSortedTableData = () => {
    if (!tableData.value || !Array.isArray(tableData.value)) {
      return []
    }
    
    const sortedData = [...tableData.value]
    
    // 如果沒有排序條件，按 post_id 降序排列
    if (Object.keys(sortDirection.value).length === 0) {
      return sortedData.sort((a, b) => b.post_id - a.post_id)
    }
    
    const sortKey = Object.keys(sortDirection.value)[0]
    const direction = sortDirection.value[sortKey]
    
    return sortedData.sort((a, b) => {
      let valueA, valueB
      
      switch (sortKey) {
        case 'revenues':
          valueA = parseFloat(a.total_revenue || 0)
          valueB = parseFloat(b.total_revenue || 0)
          break
        case 'likes':
          valueA = parseInt(a.total_likes || 0)
          valueB = parseInt(b.total_likes || 0)
          break
        case 'views':
          valueA = parseMetricValue(a.total_views || 0)
          valueB = parseMetricValue(b.total_views || 0)
          break
        case 'download':
          valueA = parseInt(a.total_downloads || 0)
          valueB = parseInt(b.total_downloads || 0)
          break
        case 'saved':
          valueA = parseInt(a.save_count || 0)
          valueB = parseInt(b.save_count || 0)
          break
        case 'rating':
          valueA = parseFloat(a.post_rating || 0)
          valueB = parseFloat(b.post_rating || 0)
          break
        default:
          return 0
      }
      
      return direction === 'desc' ? valueB - valueA : valueA - valueB
    })
  }

  // 獲取排序指示器的 CSS 類別
  const getSortClass = (sortKey) => {
    if (!sortDirection.value[sortKey]) return ''
    return sortDirection.value[sortKey] === 'asc' ? 'asc' : 'desc'
  }

  // ===== 展開收起管理 =====

  // 切換貼文收入詳情展開狀態
  const togglePostRevenue = async (postId) => {
    expandedPosts.value[postId] = !expandedPosts.value[postId]
    
    // 如果展開且還沒有詳情數據，則獲取數據
    if (expandedPosts.value[postId] && !revenueDetails.value[postId]) {
      try {
        const data = await fetchIndividualPostRevenuesDetails(
          chartParams.start,
          chartParams.end,
          postId
        )
        revenueDetails.value[postId] = data.individual_post_revenues || []
      } catch (error) {
        console.error('Error fetching revenue details:', error)
        // 如果獲取失敗，關閉展開狀態
        expandedPosts.value[postId] = false
      }
    }
  }

  // 檢查貼文是否已展開
  const isPostExpanded = (postId) => {
    return !!expandedPosts.value[postId]
  }

  // 獲取貼文收入詳情
  const getPostRevenueDetails = (postId) => {
    return revenueDetails.value[postId] || []
  }

  // ===== 日期處理方法 =====

  // 原本的 calculatePreviousDateRange
  const calculatePreviousDateRange = () => {
    const startDate = new Date(chartParams.start)
    const endDate = new Date(chartParams.end)
    const differenceInDays = ((endDate - startDate) / (1000 * 60 * 60 * 24)).toFixed(0)

    chartParams.previousStart = format(subDays(startDate, differenceInDays), 'yyyy/MM/dd')
    chartParams.previousEnd = format(subDays(endDate, differenceInDays), 'yyyy/MM/dd')
  }

  // 清除快取的方法
  const clearCache = () => {
    const oldSize = cacheStorage.size()
    const clearedCount = cacheStorage.clear()
  }

  // 原本的 updateDateRange
  const updateDateRange = (dateRange) => {
    chartParams.start = dateRange.start
    chartParams.end = dateRange.end
    calculatePreviousDateRange()
    // 日期範圍變更時清除快取
    clearCache()
  }

  // 原本的 updateSelectedModels
  const updateSelectedModels = (postIds) => {
    chartParams.postIds = postIds
    // 模型選擇變更時清除快取
    clearCache()
  }


  // 日期格式化方法
  const formatDateTip = () => {
    return {
      previousStart: format(parse(chartParams.previousStart, 'yyyy/MM/dd', new Date()), 'MMMM dd, yyyy'),
      previousEnd: format(parse(chartParams.previousEnd, 'yyyy/MM/dd', new Date()), 'MMMM dd, yyyy')
    }
  }

  const formatValue = (value, type = 'number') => {
    // 處理空值和無效值
    if (value === null || value === undefined || value === '') {
      return type === 'currency' || type === 'revenues' ? '$0.00' : '0'
    }
    
    const numValue = parseFloat(value) || 0
    
    switch(type) {
      case 'currency':
      case 'revenues':
        // 貨幣格式：處理大數值的縮寫
        if (numValue >= 1000) {
          return '$' + (numValue / 1000).toFixed(1) + 'K'
        }
        return '$' + numValue.toFixed(2)
        
      case 'rating':
        // 評分格式：固定一位小數
        return numValue.toFixed(1)
        
      case 'percentage':
        // 百分比格式
        return numValue.toFixed(1) + '%'
        
      case 'number':
      default:
        // 普通數字格式：處理大數值縮寫
        if (numValue >= 1000) {
          return (numValue / 1000).toFixed(1) + 'K'
        }
        return Math.round(numValue).toString()
    }
  }

  // 獲取貼文連結
  const getPostLink = (post) => {
    // 兼容不同數據格式：analysis API 使用 post_state/post_id，models API 使用 state/id
    const state = post.post_state || post.state
    const id = post.post_id || post.id
    
    return state === 'published' 
      ? `/posts/${id}` 
      : `/posts/${id}/preview`
  }

  // ===== 主要業務邏輯方法 =====

  // 初始化方法
  const initializeAnalytics = async () => {
    await fetchModels()
  }

  // ===== 返回公共介面 =====
  return {
    // 響應式狀態
    chartParams,
    chartData,
    loading,
    posts,
    summaryData,
    tableData,
    error,

    // 數據處理方法
    getDateRange,
    getTotalList,

    // 統計概覽方法
    getGrowthRateClass,
    getColSpanClass,

    // 表格排序方法
    sortPostsBy,
    getSortedTableData,
    getSortClass,

    // 展開收起方法
    togglePostRevenue,
    isPostExpanded,
    getPostRevenueDetails,

    // 格式化方法
    formatValue,
    getPostLink,
    formatDateTip,

    // 日期處理方法
    updateDateRange,
    updateSelectedModels,

    // 主要業務邏輯
    fetchAnalyticsData,
    fetchIndividualPostRevenuesDetails,
    initializeAnalytics
  }
}
